import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Check, X, CheckCircle2, FileText, ArrowRight } from "lucide-react";

const TABS = [
  { key: "academic", label: "Academy Admission" },
  { key: "hostel",   label: "Hostel Admission" },
];

export default function Admission() {
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("academic");
  useEffect(() => { api.get("/public/admission").then((r) => setContent(r.data || {})).catch(() => setContent({})); }, []);
  if (!content) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  const data = content[tab] || {};

  return (
    <div data-testid="admission-page">
      {/* Hero */}
      <section className="relative border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0">
          <img src={data.hero_image || "https://images.unsplash.com/photo-1526676037777-05a232554d77?w=2000"} alt=""
            className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0B1120]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none">{data.title}</h1>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm md:text-base font-medium text-white/70">
            <Link to="/" className="hover:brand-orange">Home</Link>
            <ChevronRight size={14} className="brand-orange" />
            <span className="brand-orange">Admission</span>
          </div>
        </div>
      </section>

      {/* Tab pills */}
      <div className="sticky top-16 z-30 bg-[#0B1120]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex gap-1 py-2.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admission-tab-${t.key}`}
              className={`px-4 py-1.5 text-[11px] md:text-xs font-mono uppercase tracking-widest ${tab === t.key ? "bg-brand-orange text-black" : "text-white/60 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Watermark title */}
      <div className="relative py-10 md:py-14 text-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <div className="font-display text-[100px] md:text-[160px] font-black uppercase tracking-tighter text-white/[0.04] leading-none whitespace-nowrap">
            ADMISSION
          </div>
        </div>
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-[0.4em] brand-orange mb-2">{data.title}</div>
          <div className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter">{data.title}</div>
          {data.subtitle && <p className="mt-3 text-white/60 max-w-2xl mx-auto px-5">{data.subtitle}</p>}
        </div>
      </div>

      {/* Intro */}
      {data.intro && (
        <div className="max-w-4xl mx-auto px-5 md:px-8 pb-10">
          <p className="text-white/70 text-base md:text-lg leading-relaxed">{data.intro}</p>
        </div>
      )}

      {/* Fees table — grouped by package */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 pb-10" data-testid="admission-fees">
        <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 brand-orange">Fees & Charges</h3>
        {(() => {
          const groups = {};
          const order = [];
          (data.fees || []).forEach((f) => {
            const pkg = f.package || "General";
            if (!groups[pkg]) { groups[pkg] = []; order.push(pkg); }
            groups[pkg].push(f);
          });
          return order.map((pkg) => {
            const rows = groups[pkg];
            const pkgTotal = rows.reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);
            const pkgRef = rows.filter((f) => f.refundable).reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);
            return (
              <div key={pkg} className="mb-6 border border-white/12" data-testid={`fee-package-${pkg.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="bg-brand-blue px-4 py-2 flex items-center justify-between gap-3">
                  <div className="font-display text-base md:text-lg font-black uppercase tracking-tight text-white">{pkg}</div>
                  <div className="font-mono text-xs text-white/80">
                    Total: ₹{pkgTotal.toLocaleString("en-IN")}
                    {pkgRef > 0 && <span className="ml-2 opacity-75">· Refundable ₹{pkgRef.toLocaleString("en-IN")}</span>}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map((f, i) => (
                      <tr key={i} className="border-b border-white/6 hover:bg-white/4 last:border-b-0">
                        <td className="px-4 py-3 font-medium w-1/3">{f.label}</td>
                        <td className="px-4 py-3 text-right font-mono w-32">₹{(parseFloat(f.amount) || 0).toLocaleString("en-IN")}</td>
                        <td className="px-4 py-3 text-center w-28">
                          {f.refundable
                            ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest brand-blue"><Check size={12} /> Refundable</span>
                            : <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40"><X size={12} /> Non-refund</span>}
                        </td>
                        <td className="px-4 py-3 text-white/60 text-xs">{f.description || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          });
        })()}
      </div>

      {/* Wallet / imprest note */}
      {data.wallet_note && (
        <div className="max-w-5xl mx-auto px-5 md:px-8 pb-10" data-testid="admission-wallet-note">
          <div className="border-l-4 border-brand-orange bg-brand-orange/10 p-5 md:p-6">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">Ward Wallet · Imprest System</div>
            <p className="text-sm md:text-base text-white/80 leading-relaxed">{data.wallet_note}</p>
          </div>
        </div>
      )}

      {/* Documents */}
      {data.documents?.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 md:px-8 pb-10" data-testid="admission-documents">
          <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 brand-blue">Documents Required</h3>
          <div className="border border-white/12 bg-[#131A2B] p-5 md:p-6">
            <ul className="space-y-2">
              {data.documents.map((d, i) => (
                <li key={i} className="flex items-start gap-3">
                  <FileText size={16} className="brand-blue mt-1 shrink-0" />
                  <span className="text-sm text-white/80">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Terms */}
      {data.terms?.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 md:px-8 pb-14" data-testid="admission-terms">
          <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 brand-orange">Terms & Conditions</h3>
          <div className="border border-white/12 bg-[#131A2B] p-5 md:p-6">
            <ul className="space-y-2">
              {data.terms.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="brand-orange mt-1 shrink-0" />
                  <span className="text-sm text-white/80">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="border-t border-white/8 bg-brand-orange text-black">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="font-mono text-xs uppercase tracking-[0.3em] mb-3">// Ready to begin?</div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
              Start your ward's<br />admission today.
            </h2>
          </div>
          <div className="md:justify-self-end">
            <Link to="/enrol" className="bg-black text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-white/90 hover:text-black inline-flex items-center gap-2">
              Enrol Your Ward <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
